/**
 * Validate GL / location selections for Account Reconciliation templates.
 */

import { canonicalLocationKey } from '../../../../utils/locationName.js';
import {
  buildLocationTypeById,
  isConsolidatedLocationId,
  isConsolidatedOnlyLocationSelection,
} from '../../../../utils/locationMasterType.js';

function canonicalId(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    if (value.$oid) return String(value.$oid);
    if (value._id != null && value._id !== value) return canonicalId(value._id);
    if (value.id != null && value.id !== value) return canonicalId(value.id);
    if (value.location_id != null) return canonicalId(value.location_id);
    if (typeof value.toHexString === 'function') return value.toHexString();
    return '';
  }
  return String(value).trim();
}

function entityId(entity) {
  if (entity == null) return '';
  if (typeof entity !== 'object') return String(entity).trim();
  return (
    canonicalId(entity.location_id) ||
    canonicalId(entity.locationId) ||
    canonicalId(entity._id) ||
    canonicalId(entity.id)
  );
}

function locationMasterName(loc = {}) {
  return String(loc.name || loc.description || '').trim();
}

/**
 * Label shown in the Assign Accounts Location column — the location's own name.
 * Fall back to description or a primitive `location` string. Do not use `country`
 * or a nested location object (city rows often have country = "United States of America").
 */
function entryDisplayName(entry) {
  if (entry == null || entry === '') return '';
  if (typeof entry === 'string' || typeof entry === 'number') {
    return String(entry).trim();
  }
  if (typeof entry !== 'object') return '';

  const directLabel = entry.name || entry.description || '';
  if (directLabel !== '') return String(directLabel).trim();

  if (typeof entry.location === 'string' || typeof entry.location === 'number') {
    return String(entry.location).trim();
  }

  return '';
}

function eachLocationEntry(account = {}, visit) {
  const buckets = [account.location, account.locationId, account.location_id, account.locations];
  buckets.forEach((raw) => {
    if (raw == null || raw === '') return;
    if (typeof raw === 'string') {
      raw.split(',').forEach((part) => visit(part));
      return;
    }
    if (Array.isArray(raw)) {
      raw.forEach((item) => visit(item));
    }
  });
}

function buildLocationLookups(locations = []) {
  const masterIds = new Set();
  const nameToId = new Map();
  const idToName = new Map();

  (locations || []).forEach((loc) => {
    const id = entityId(loc);
    if (!id) return;
    masterIds.add(id);
    nameToId.set(id, id);
    const name = locationMasterName(loc);
    if (name) {
      nameToId.set(name.toLowerCase(), id);
      const canon = canonicalLocationKey(name);
      if (canon) nameToId.set(canon, id);
      idToName.set(id, name);
    }
  });

  return { masterIds, nameToId, idToName };
}

function resolveEntryMasterId(entry, { masterIds, nameToId }) {
  if (entry == null || entry === '') return '';

  if (typeof entry === 'string' || typeof entry === 'number') {
    const raw = String(entry).trim();
    if (!raw) return '';
    if (masterIds.has(raw)) return raw;
    return nameToId.get(raw.toLowerCase()) || nameToId.get(canonicalLocationKey(raw)) || '';
  }

  if (typeof entry !== 'object') return '';

  // Named rows: resolve by the location's own label, never a possibly-wrong
  // embedded `_id` (city rows often carry a country/master id). Same
  // name-first rule as entryMatchesSelected.
  const name = entryDisplayName(entry);
  if (name) {
    return nameToId.get(name.toLowerCase()) || nameToId.get(canonicalLocationKey(name)) || '';
  }

  const idCandidates = [
    entry.location_id,
    entry.locationId,
    entry._id,
    entry.id,
  ];
  for (const candidate of idCandidates) {
    const id = canonicalId(candidate);
    if (id && masterIds.has(id)) return id;
  }
  return '';
}

function resolveCoaLocationIdsWithLookups(account = {}, lookups) {
  const ids = new Set();
  eachLocationEntry(account, (entry) => {
    const id = resolveEntryMasterId(entry, lookups);
    if (id) ids.add(id);
  });
  return ids;
}

/**
 * Resolve COA row location refs to location master ids (supports id objects, names, csv).
 * Only returns ids that exist on the location master — never country or unmatched raw values.
 */
export function resolveCoaLocationIds(account = {}, locations = []) {
  return resolveCoaLocationIdsWithLookups(account, buildLocationLookups(locations));
}

function accountKeys(account = {}) {
  return [account.glcode, account._id, account.id]
    .filter((v) => v != null && String(v).trim())
    .map((v) => String(v).trim());
}

/**
 * Index COA accounts once for per-location GL filtering.
 * Each entry keeps match keys (glcode / _id / id), canonical glcode, and master location ids.
 */
export function buildAccountLocationIndex(chartOfAccounts = [], locations = []) {
  const hasLocations = (locations || []).length > 0;
  const lookups = hasLocations ? buildLocationLookups(locations) : null;
  const accounts = [];

  (chartOfAccounts || []).forEach((account) => {
    const gl = account.glcode != null ? String(account.glcode).trim() : '';
    if (!gl) return;
    const keys = accountKeys(account);
    if (!keys.length) return;
    accounts.push({
      keys,
      gl,
      locIds: lookups ? resolveCoaLocationIdsWithLookups(account, lookups) : null,
    });
  });

  return { accounts, hasLocations };
}

function entryMatchesSelected(entry, selectedIds, selectedNames, lookups) {
  const displayName = entryDisplayName(entry);
  if (displayName) {
    const canon = canonicalLocationKey(displayName);
    if (selectedNames.has(displayName.toLowerCase()) || (canon && selectedNames.has(canon))) {
      return true;
    }
    if (selectedIds.has(displayName)) return true;
    const mapped =
      lookups.nameToId.get(displayName.toLowerCase()) || lookups.nameToId.get(canon);
    if (mapped && selectedIds.has(mapped)) return true;
    // Named rows must match the selected location label/id — do not fall through
    // to a possibly-wrong embedded `_id` (or country) on city locations.
    return false;
  }

  const id = resolveEntryMasterId(entry, lookups);
  return Boolean(id && selectedIds.has(id));
}

/**
 * Keep only selected GLs that are mapped to one assignment location.
 * Used when expanding a multi-location block so each API row gets that site's GLs.
 *
 * A consolidated location keeps every selected GL. Entity locations keep the
 * COA intersection for that location id.
 *
 * When `locations` is empty, location-master lookups are unavailable, so the
 * selected list is returned unfiltered. Callers that can supply the master
 * list should pass it so per-location mapping can run.
 *
 * Pass `accountIndex` from `buildAccountLocationIndex` when filtering many
 * locations so COA/location lookups are built once per payload.
 * Pass `locationTypeById` from `buildLocationTypeById` for the same reason.
 */
export function filterGlcodesForLocation({
  glcodes = [],
  locationId,
  chartOfAccounts = [],
  locations = [],
  accountIndex,
  locationTypeById,
} = {}) {
  const locId =
    typeof locationId === 'object' && locationId !== null
      ? canonicalId(locationId)
      : String(locationId || '').trim();
  const selected = (glcodes || []).map(String).filter(Boolean);
  if (!locId || !selected.length) return [];

  const typeById = locationTypeById || buildLocationTypeById(locations);
  if (isConsolidatedLocationId(locId, typeById)) {
    return [...new Set(selected)];
  }

  const index = accountIndex || buildAccountLocationIndex(chartOfAccounts, locations);
  if (!index.hasLocations) return selected;

  const selectedSet = new Set(selected);
  const matched = [];

  index.accounts.forEach(({ keys, gl, locIds }) => {
    if (!keys.some((k) => selectedSet.has(k))) return;
    if (!locIds.has(locId)) return;
    matched.push(gl);
  });

  return [...new Set(matched.filter(Boolean))];
}

/**
 * Drop assignment GLs that are no longer in the Accounts-step pool.
 * Matches on any COA key (glcode / _id / id) of the pool accounts.
 */
export function pruneAssignmentGlcodesToPool(assignments = [], pool = [], chartOfAccounts = []) {
  const allowed = new Set((pool || []).map((v) => String(v).trim()).filter(Boolean));
  (chartOfAccounts || []).forEach((account) => {
    const keys = accountKeys(account);
    if (keys.some((k) => allowed.has(k))) keys.forEach((k) => allowed.add(k));
  });
  return (assignments || []).map((a) => ({
    ...a,
    glcodes: [...(a?.glcodes || a?.glcode || [])]
      .map(String)
      .filter((g) => allowed.has(g.trim())),
  }));
}

/**
 * Accounts-tab GLs mapped to ANY of the selected assignment locations.
 * Matching uses the same location name shown in the table so a city GL
 * (San Francisco, country = United States) is not treated as the USA location.
 *
 * When every selected location is consolidated, every pool account is returned
 * regardless of its COA location. Entity-only and mixed selections keep the
 * strict location match.
 */
export function filterAccountsForAssignment({
  chartOfAccounts = [],
  availableGlCodes = [],
  locationIds = [],
  locations = [],
} = {}) {
  const pool = (availableGlCodes || []).map(String).filter(Boolean);
  const selectedLocIds = (locationIds || [])
    .map((id) => {
      if (typeof id === 'object' && id !== null) return canonicalId(id);
      return String(id).trim();
    })
    .filter(Boolean);
  if (!selectedLocIds.length) return [];

  const poolSet = new Set(pool);
  const lookups = buildLocationLookups(locations);
  const selectedIds = new Set();
  selectedLocIds.forEach((id) => {
    const mapped = lookups.nameToId.get(id.toLowerCase());
    if (mapped) selectedIds.add(mapped);
    else selectedIds.add(id);
  });

  if (!pool.length) return [];

  if (isConsolidatedOnlyLocationSelection([...selectedIds], locations)) {
    return (chartOfAccounts || []).filter((account) =>
      accountKeys(account).some((k) => poolSet.has(k))
    );
  }

  const selectedNames = new Set();
  selectedIds.forEach((id) => {
    const name = lookups.idToName.get(id);
    if (name) {
      selectedNames.add(name.toLowerCase());
      const canon = canonicalLocationKey(name);
      if (canon) selectedNames.add(canon);
    }
    selectedNames.add(String(id).toLowerCase());
  });

  return (chartOfAccounts || []).filter((account) => {
    const keys = accountKeys(account);
    if (!keys.length || !keys.some((k) => poolSet.has(k))) return false;

    let matched = false;
    eachLocationEntry(account, (entry) => {
      if (!matched && entryMatchesSelected(entry, selectedIds, selectedNames, lookups)) {
        matched = true;
      }
    });
    return matched;
  });
}

export function validateGlLocationAssignment({
  taskType,
  accountReconciliationType,
  assignments = [],
  chartOfAccounts = [],
  errors = {},
  locationHeader = 'Location',
}) {
  const nextErrors = { ...errors };
  const isAR = taskType === accountReconciliationType;
  const locLabel = locationHeader || 'Location';

  if (!isAR) return nextErrors;

  const coaGlcodes = new Set(
    (chartOfAccounts || [])
      .map((c) => (c.glcode != null ? String(c.glcode).trim() : ''))
      .filter(Boolean)
  );
  const coaIds = new Set(
    (chartOfAccounts || []).map((c) => c._id || c.id || c.glcode).filter(Boolean).map(String)
  );

  assignments.forEach((assignment, index) => {
    const key = `assignment_${index}`;
    if (!assignment.location_ids?.length) {
      nextErrors[key] = nextErrors[key] || `Select at least one ${locLabel}`;
    }
    const glcodes = assignment.glcodes || assignment.glcode || [];
    if (!glcodes.length) {
      nextErrors[`${key}_gl`] = 'Select at least one account';
    } else {
      const invalid = glcodes.filter((id) => {
        const value = String(id);
        return !coaGlcodes.has(value) && !coaIds.has(value);
      });
      if (invalid.length) {
        nextErrors[`${key}_gl`] = 'One or more accounts are not in Chart of Accounts';
      }
    }
  });

  return nextErrors;
}

export function hasGlLocationErrors(errors = {}) {
  return Object.values(errors).some(Boolean);
}
